<template>
  <div>
    <HrPageLayout
      title="Trainings Catalog"
      subtitle="Manage training courses and certifications"
      icon="📚"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Training & Development' },
        { label: 'Trainings Catalog' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="openAdd">
          <i class="bx bx-plus"></i> Add Training
        </button>
      </template>

      <div class="certifications-content">
        <div v-if="message" class="hr-alert" :class="messageSuccess ? 'hr-alert-success' : 'hr-alert-danger'">
          {{ message }}
          <button type="button" class="hr-btn hr-btn-sm" @click="message = ''">&times;</button>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="hr-search-wrap">
              <input v-model="searchKeyword" class="hr-input" type="text" placeholder="Search trainings..." @input="loadTrainings">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <select v-model="filterType" @change="loadTrainings" class="hr-select" style="min-width: 140px;">
                <option value="">All types</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
                <option value="online">Online</option>
                <option value="certification">Certification</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
              </select>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading trainings...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Provider</th>
                    <th>Duration</th>
                    <th>Cost</th>
                    <th>Session dates</th>
                    <th>Audience</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(t, idx) in trainings" :key="t.trainingID">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ t.trainingCode }}</td>
                    <td>{{ t.trainingName }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ t.trainingType }}</span></td>
                    <td>{{ t.category || '—' }}</td>
                    <td>{{ t.provider || '—' }}</td>
                    <td>{{ t.duration_days ? t.duration_days + 'd' : '' }} {{ t.duration_hours ? t.duration_hours + 'h' : '—' }}</td>
                    <td>{{ t.cost != null ? (t.currency || 'KES') + ' ' + t.cost : '—' }}</td>
                    <td>{{ formatSessionDates(t) }}</td>
                    <td>{{ formatAudience(t) }}</td>
                    <td><span class="hr-badge" :class="t.is_active == 1 ? 'hr-badge-success' : 'hr-badge-secondary'">{{ t.is_active == 1 ? 'Yes' : 'No' }}</span></td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openEdit(t)"><i class="bx bx-edit"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="confirmDelete(t)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="trainings.length === 0">
                    <td colspan="12" class="hr-text-center hr-py-4 hr-text-muted">No trainings found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Add/Edit Modal -->
    <div class="hr-modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="hr-modal hr-modal-large">
        <div class="hr-modal-header">
          <h3>{{ editingId ? 'Edit Training' : 'Add Training' }}</h3>
          <button type="button" class="hr-modal-close" @click="showModal = false">&times;</button>
        </div>
        <form @submit.prevent="saveTraining" class="hr-form-grid hr-p-3">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Code</label>
            <input v-model="form.trainingCode" class="hr-input" placeholder="e.g. TR-SAFETY01">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Name <span class="hr-text-danger">*</span></label>
            <input v-model="form.trainingName" class="hr-input" required placeholder="Training name">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Type</label>
            <select v-model="form.trainingType" class="hr-select">
              <option value="internal">Internal</option>
              <option value="external">External</option>
              <option value="online">Online</option>
              <option value="certification">Certification</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Category</label>
            <input v-model="form.category" class="hr-input" placeholder="Category">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Provider</label>
            <input v-model="form.provider" class="hr-input" placeholder="Provider">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Duration (days / hours)</label>
            <div class="hr-flex gap-2">
              <input v-model.number="form.duration_days" class="hr-input" type="number" min="0" placeholder="Days">
              <input v-model.number="form.duration_hours" class="hr-input" type="number" min="0" placeholder="Hours">
            </div>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Cost</label>
            <input v-model.number="form.cost" class="hr-input" type="number" step="0.01" min="0" placeholder="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Currency</label>
            <input v-model="form.currency" class="hr-input" placeholder="KES">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea v-model="form.description" class="hr-textarea" rows="2" placeholder="Description"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Mandatory</label>
            <input type="checkbox" v-model="form.is_mandatory" :true-value="1" :false-value="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Active</label>
            <input type="checkbox" v-model="form.is_active" :true-value="1" :false-value="0">
          </div>

          <div class="hr-filter-group span-full schedule-section">
            <h4 class="schedule-heading">Schedule &amp; audience</h4>
            <p class="hr-text-muted hr-small">Set dates so this training appears on the Training Calendar and in My Training.</p>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Start date <span class="hr-text-danger">*</span></label>
            <input v-model="form.start_date" class="hr-input" type="date" required>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">End date <span class="hr-text-danger">*</span></label>
            <input v-model="form.end_date" class="hr-input" type="date" required>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Session venue</label>
            <input v-model="form.venue" class="hr-input" placeholder="e.g. County HQ boardroom">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Audience</label>
            <select v-model="form.audience_mode" class="hr-select">
              <option value="all">All employees</option>
              <option value="departments">Specific departments</option>
            </select>
          </div>
          <div v-if="form.audience_mode === 'departments'" class="hr-filter-group span-full dept-pick">
            <label class="hr-form-label">Departments <span class="hr-text-danger">*</span></label>
            <div class="dept-check-grid">
              <label v-for="d in departments" :key="d.id" class="dept-check">
                <input type="checkbox" :value="Number(d.id)" v-model="form.department_ids">
                {{ d.departmentName }}
              </label>
            </div>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Session notes</label>
            <textarea v-model="form.schedule_notes" class="hr-textarea" rows="2" placeholder="Optional instructions for attendees"></textarea>
          </div>

          <div class="hr-modal-footer span-full">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">
              <span v-if="saving" class="hr-spinner small"></span>
              <i v-else class="bx bx-save"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm -->
    <div class="hr-modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header">
          <h3>Delete Training</h3>
          <button type="button" class="hr-modal-close" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="hr-p-3">
          <p>Delete "{{ deleteTarget && deleteTarget.trainingName }}"? This cannot be undone.</p>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="deleteTarget = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="doDelete" :disabled="deleting">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "TrainingsCatalog",
  components: { HrPageLayout },
  data() {
    return {
      trainings: [],
      loading: false,
      message: '',
      messageSuccess: false,
      searchKeyword: '',
      filterType: '',
      showModal: false,
      editingId: 0,
      scheduleId: 0,
      saving: false,
      departments: [],
      scheduleMap: {},
      form: {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1,
        start_date: '',
        end_date: '',
        venue: '',
        audience_mode: 'all',
        department_ids: [],
        schedule_notes: ''
      },
      deleteTarget: null,
      deleting: false
    };
  },
  mounted() {
    this.loadDepartments();
    this.loadTrainings();
  },
  methods: {
    async loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      try {
        const res = await execute(data, authUrl);
        const payload = res?.data?.data;
        this.departments = Array.isArray(payload?.departments) ? payload.departments : (Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (_) {
        this.departments = [];
      }
    },
    defaultForm() {
      return {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1,
        start_date: '',
        end_date: '',
        venue: '',
        audience_mode: 'all',
        department_ids: [],
        schedule_notes: ''
      };
    },
    formatSessionDates(t) {
      const s = this.scheduleMap[t.trainingID];
      if (!s) return '—';
      return s.start_date === s.end_date ? s.start_date : `${s.start_date} – ${s.end_date}`;
    },
    formatAudience(t) {
      const s = this.scheduleMap[t.trainingID];
      if (!s) return '—';
      if (s.audience_mode === 'all') return 'All employees';
      const names = (s.departments || []).map(d => d.departmentName).filter(Boolean);
      return names.length ? names.join(', ') : 'Departments';
    },
    async loadTrainings() {
      this.loading = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "getTrainings");
      if (this.filterType) data.append("trainingType", this.filterType);
      data.append("activeOnly", "0");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          let list = res.data.data.trainings || [];
          const q = (this.searchKeyword || '').toLowerCase();
          if (q) {
            list = list.filter(t =>
              (t.trainingName && t.trainingName.toLowerCase().includes(q)) ||
              (t.trainingCode && t.trainingCode.toLowerCase().includes(q)) ||
              (t.category && t.category.toLowerCase().includes(q))
            );
          }
          this.trainings = list;
          await this.loadScheduleMap(list);
        }
      } catch (e) {
        this.message = e.message || 'Failed to load trainings';
        this.messageSuccess = false;
      } finally {
        this.loading = false;
      }
    },
    async loadScheduleMap(list) {
      const map = {};
      for (const t of list) {
        const fd = new FormData();
        fd.append("function", "getTrainingSchedulesForTraining");
        fd.append("trainingID", t.trainingID);
        try {
          const res = await execute(fd, authUrl);
          const schedules = res.data?.data?.schedules || [];
          if (schedules.length) map[t.trainingID] = schedules[0];
        } catch (_) { /* ignore */ }
      }
      this.scheduleMap = map;
    },
    openAdd() {
      this.editingId = 0;
      this.scheduleId = 0;
      this.form = this.defaultForm();
      this.showModal = true;
    },
    async openEdit(t) {
      this.editingId = t.trainingID;
      this.scheduleId = 0;
      this.form = {
        ...this.defaultForm(),
        trainingCode: t.trainingCode || '',
        trainingName: t.trainingName || '',
        trainingType: t.trainingType || 'internal',
        category: t.category || '',
        provider: t.provider || '',
        duration_days: t.duration_days ?? null,
        duration_hours: t.duration_hours ?? null,
        cost: t.cost != null ? t.cost : null,
        currency: t.currency || 'KES',
        description: t.description || '',
        is_mandatory: t.is_mandatory ?? 0,
        is_active: t.is_active != null ? t.is_active : 1
      };
      const sched = this.scheduleMap[t.trainingID];
      if (sched) {
        this.applyScheduleToForm(sched);
      } else {
        await this.loadScheduleForEdit(t.trainingID);
      }
      this.showModal = true;
    },
    applyScheduleToForm(sched) {
      this.scheduleId = sched.id || sched.schedule_id || 0;
      this.form.start_date = sched.start_date || '';
      this.form.end_date = sched.end_date || '';
      this.form.venue = sched.venue || '';
      this.form.audience_mode = sched.audience_mode || 'all';
      this.form.schedule_notes = sched.notes || '';
      this.form.department_ids = (sched.departments || []).map(d => Number(d.department_id)).filter(n => n > 0);
    },
    async loadScheduleForEdit(trainingID) {
      const fd = new FormData();
      fd.append("function", "getTrainingSchedulesForTraining");
      fd.append("trainingID", trainingID);
      try {
        const res = await execute(fd, authUrl);
        const schedules = res.data?.data?.schedules || [];
        if (schedules.length) this.applyScheduleToForm(schedules[0]);
      } catch (_) { /* ignore */ }
    },
    async saveTraining() {
      if (!this.form.start_date || !this.form.end_date) {
        this.message = 'Start and end dates are required.';
        this.messageSuccess = false;
        return;
      }
      if (this.form.start_date > this.form.end_date) {
        this.message = 'End date must be on or after start date.';
        this.messageSuccess = false;
        return;
      }
      if (this.form.audience_mode === 'departments' && (!this.form.department_ids || !this.form.department_ids.length)) {
        this.message = 'Select at least one department.';
        this.messageSuccess = false;
        return;
      }
      this.saving = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "saveTraining");
      data.append("trainingID", this.editingId);
      data.append("trainingCode", this.form.trainingCode);
      data.append("trainingName", this.form.trainingName);
      data.append("trainingType", this.form.trainingType);
      data.append("category", this.form.category);
      data.append("provider", this.form.provider);
      data.append("duration_days", this.form.duration_days || 0);
      data.append("duration_hours", this.form.duration_hours || 0);
      if (this.form.cost != null && this.form.cost !== '') data.append("cost", this.form.cost);
      data.append("currency", this.form.currency);
      data.append("description", this.form.description);
      data.append("is_mandatory", this.form.is_mandatory ? '1' : '0');
      data.append("is_active", this.form.is_active ? '1' : '0');
      data.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const trainingID = res.data.data?.trainingID || this.editingId;
          const schedOk = await this.saveSchedule(trainingID);
          if (!schedOk) {
            this.message = this.message || 'Training saved but schedule failed.';
            this.messageSuccess = false;
            return;
          }
          this.message = res.data.message || 'Saved successfully';
          this.messageSuccess = true;
          this.showModal = false;
          this.loadTrainings();
        } else {
          this.message = res.data.message || 'Save failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Save failed';
        this.messageSuccess = false;
      } finally {
        this.saving = false;
      }
    },
    async saveSchedule(trainingID) {
      const fd = new FormData();
      fd.append("function", "saveTrainingSchedule");
      if (this.scheduleId) fd.append("scheduleID", this.scheduleId);
      fd.append("trainingID", trainingID);
      fd.append("start_date", this.form.start_date);
      fd.append("end_date", this.form.end_date);
      fd.append("venue", this.form.venue || '');
      fd.append("audience_mode", this.form.audience_mode);
      fd.append("notes", this.form.schedule_notes || '');
      fd.append("department_ids", JSON.stringify(this.form.department_ids || []));
      fd.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.scheduleId = res.data.data?.scheduleID || this.scheduleId;
          return true;
        }
        this.message = res.data?.message || 'Schedule save failed';
        return false;
      } catch (e) {
        this.message = e.message || 'Schedule save failed';
        return false;
      }
    },
    confirmDelete(t) {
      this.deleteTarget = t;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteTraining");
      data.append("trainingID", this.deleteTarget.trainingID);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = 'Training deleted';
          this.messageSuccess = true;
          this.deleteTarget = null;
          this.loadTrainings();
        } else {
          this.message = res.data.message || 'Delete failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Delete failed';
        this.messageSuccess = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.certifications-content { padding: 0; }
.hr-alert { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; }
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
.hr-flex.gap-2 { display: flex; gap: 8px; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.hr-modal { background: #fff; border-radius: 16px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
.hr-modal-medium { width: 560px; }
.hr-modal-large { width: 720px; }
.schedule-heading { margin: 0 0 4px; font-size: 1rem; color: #0f172a; }
.schedule-section { border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 4px; }
.hr-small { font-size: 0.875rem; }
.dept-check-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; max-height: 160px; overflow-y: auto; padding: 8px; border: 1px solid #e2e8f0; border-radius: 8px; }
.dept-check { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; margin: 0; }
.hr-modal-small { width: 400px; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #64748b; }
.hr-modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hr-p-3 { padding: 20px; }
.hr-mt-3 { margin-top: 16px; }
</style>
